import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { articleGenerationService } from '../../services/articleGenerationService';
import { ArticleCommentRow, ArticleGenerationRow } from '../../types/articleGeneration';

interface Props {
  run: ArticleGenerationRow;
}

const CommentsPanel: React.FC<Props> = ({ run }) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState<ArticleCommentRow[]>([]);
  const [body, setBody] = useState('');
  const [target, setTarget] = useState<string>(''); // '' = whole article, else section index
  const [busy, setBusy] = useState(false);

  const reload = () =>
    articleGenerationService
      .listComments(run.id)
      .then(setComments)
      .catch(() => setComments([]));

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.id]);

  const sectionOptions = useMemo(
    () =>
      (run.sections ?? []).map((s) => ({
        index: s.index,
        label: s.index === 0 ? t('article.comment_lead') : s.heading || `#${s.index}`,
      })),
    [run.sections, t]
  );

  const sectionLabel = (index: number | null): string => {
    if (index === null) return t('article.comment_whole_article');
    if (index === 0) return t('article.comment_lead');
    return run.sections?.find((s) => s.index === index)?.heading || `#${index}`;
  };

  const submit = async () => {
    const text = body.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      await articleGenerationService.addComment(
        run.id,
        target === '' ? null : Number(target),
        text
      );
      setBody('');
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    await articleGenerationService.deleteComment(id);
    await reload();
  };

  return (
    <section>
      <h3 className="font-semibold mb-3">{t('article.comments_title')}</h3>

      <div className="space-y-2 mb-4">
        {comments.length === 0 && (
          <div className="text-sm text-gray-500">{t('article.comments_empty')}</div>
        )}
        {comments.map((c) => (
          <div key={c.id} className="border rounded-lg p-3 bg-white">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-xs font-medium text-gray-500">
                {sectionLabel(c.section_index)} · {new Date(c.created_at).toLocaleString()}
              </span>
              <button
                type="button"
                onClick={() => remove(c.id)}
                className="text-xs text-red-600 hover:underline"
              >
                {t('article.comment_delete')}
              </button>
            </div>
            <div className="text-sm whitespace-pre-wrap">{c.body}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          aria-label={t('article.comment_target')}
          className="p-2 border rounded-md text-sm sm:w-56"
        >
          <option value="">{t('article.comment_whole_article')}</option>
          {sectionOptions.map((s) => (
            <option key={s.index} value={s.index}>
              {s.label}
            </option>
          ))}
        </select>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t('article.comment_placeholder')}
          rows={2}
          className="flex-1 p-2 border rounded-md text-sm"
        />
        <button
          type="button"
          onClick={submit}
          disabled={!body.trim() || busy}
          className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-semibold disabled:bg-gray-400"
        >
          {t('article.comment_add')}
        </button>
      </div>
    </section>
  );
};

export default CommentsPanel;
